/**
 * UserPermissionsEditor Component
 * Allows admins to manage granular permission overrides for a user
 *
 * Displays all permissions grouped by category, showing:
 * - Role default (what Staff/Admin normally gets)
 * - User override (if any)
 * - Toggle to grant/deny each permission
 */

import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@/services/api';
import { useConfirmDialog, ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { WaveLoader } from '@/components/ui/WaveLoader';

interface Permission {
  id: string;
  category: string;
  name: string;
  description: string;
  granted: boolean;
  source: 'role_default' | 'user_override';
  roleDefault: boolean;
  override?: {
    grantedBy: string;
    grantedByName: string;
    grantedByEmail: string;
    grantedAt: string;
    reason: string | null;
  };
}

interface UserPermissionsResponse {
  userId: string;
  role: string;
  permissions: Permission[];
}

interface UserPermissionsEditorProps {
  userId: string;
  userName: string;
  userRole: string;
  isPartner?: boolean;
  onClose?: () => void;
  onRoleChanged?: () => void;
}

// Group permissions by category
const groupByCategory = (permissions: Permission[]): Record<string, Permission[]> => {
  return permissions.reduce((acc, perm) => {
    if (!acc[perm.category]) {
      acc[perm.category] = [];
    }
    acc[perm.category].push(perm);
    return acc;
  }, {} as Record<string, Permission[]>);
};

// Category display names
const categoryNames: Record<string, string> = {
  deposits: 'Deposits',
  allocations: 'Allocations',
  assignments: 'Assignments',
  periods: 'Entry Periods',
  files: 'Files',
  notes: 'Notes',
  audit: 'Audit',
  users: 'Users',
  clients: 'Clients',
  bulk: 'Bulk Operations',
  reports: 'Reports',
};

/**
 * Permission ids currently read by a backend route guard. Anything else in the
 * catalog is stored and displayed, but toggling it does not change API access.
 */
export const ENFORCED_PERMISSION_IDS = new Set<string>([
  'deposits.create',
  'deposits.edit',
  'deposits.delete',
  'deposits.import',
  'notes.create',
  'files.view',
  'files.upload',
  'files.delete_own',
  'files.delete_any',
  'periods.create',
  'periods.edit',
  'periods.close',
  'periods.reopen',
  'clients.create',
  'clients.edit',
  'bulk.status',
  'bulk.allocate',
  'bulk.delete',
  'bulk.export',
  'reports.view',
  'reports.export',
  'reports.complete',
  'audit.view_system',
  'audit.export',
  'audit.view_deposit',
  'assignments.assign_self',
  'assignments.assign_others',
  'assignments.unassign',
]);

// Category icons
const categoryIcons: Record<string, string> = {
  deposits: 'fa-receipt',
  allocations: 'fa-divide',
  assignments: 'fa-user-tag',
  periods: 'fa-calendar-alt',
  files: 'fa-file-alt',
  notes: 'fa-sticky-note',
  audit: 'fa-history',
  users: 'fa-users',
  clients: 'fa-building',
  bulk: 'fa-layer-group',
  reports: 'fa-chart-bar',
};

export const UserPermissionsEditor: React.FC<UserPermissionsEditorProps> = ({
  userId,
  userName,
  userRole,
  isPartner,
  onClose,
  onRoleChanged,
}) => {
  const queryClient = useQueryClient();
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [pendingOverrides, setPendingOverrides] = useState<Map<string, boolean>>(new Map());
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'success' | 'error'>('idle');
  const { confirm, dialogProps } = useConfirmDialog();

  // Convert to Admin mutation
  const convertToAdmin = useMutation({
    mutationFn: async () => {
      const { data } = await api.patch(`/admin/users/${userId}`, { role: 'Admin' });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      queryClient.invalidateQueries({ queryKey: ['permissions', 'user', userId] });
      onRoleChanged?.();
    },
  });

  const handleConvertToAdmin = () => {
    confirm({
      title: 'Convert to Admin',
      message: `Are you sure you want to promote ${userName} to Admin? They will have full access to all clients and all permissions. This replaces all permission overrides.`,
      confirmLabel: 'Convert to Admin',
      type: 'warning',
      onConfirm: () => convertToAdmin.mutate(),
    });
  };

  // Fetch user's permissions
  const { data, isLoading, error } = useQuery<UserPermissionsResponse>({
    queryKey: ['permissions', 'user', userId],
    queryFn: async (): Promise<UserPermissionsResponse> => {
      const response = await api.get<UserPermissionsResponse>(`/permissions/users/${userId}`);
      // The API returns the permissions response directly (not wrapped in ApiResponse)
      return response.data as unknown as UserPermissionsResponse;
    },
    staleTime: 30 * 1000,
  });

  // Save overrides mutation
  const saveOverrides = useMutation({
    mutationFn: async (overrides: Array<{ permissionId: string; granted: boolean }>) => {
      const { data } = await api.put(`/permissions/users/${userId}`, { overrides });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['permissions', 'user', userId] });
      queryClient.invalidateQueries({ queryKey: ['permissions', 'me'] });
      setPendingOverrides(new Map());
      setSaveStatus('success');
      setTimeout(() => setSaveStatus('idle'), 2000);
    },
    onError: () => {
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    },
  });

  // Clear all overrides mutation
  const clearOverrides = useMutation({
    mutationFn: async () => {
      const { data } = await api.delete(`/permissions/users/${userId}`);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['permissions', 'user', userId] });
      queryClient.invalidateQueries({ queryKey: ['permissions', 'me'] });
    },
  });

  // Group permissions by category
  const groupedPermissions = useMemo(() => {
    if (!data?.permissions) return {};
    return groupByCategory(data.permissions);
  }, [data?.permissions]);

  // Count overrides
  const overrideCount = useMemo(() => {
    if (!data?.permissions) return 0;
    return data.permissions.filter(p => p.source === 'user_override').length;
  }, [data?.permissions]);

  const toggleCategory = (category: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  const handleTogglePermission = (permissionId: string, currentGranted: boolean, roleDefault: boolean) => {
    const newGranted = !currentGranted;

    // If toggling back to role default, remove from pending
    if (newGranted === roleDefault) {
      setPendingOverrides(prev => {
        const next = new Map(prev);
        next.delete(permissionId);
        return next;
      });
    } else {
      // Add to pending overrides
      setPendingOverrides(prev => {
        const next = new Map(prev);
        next.set(permissionId, newGranted);
        return next;
      });
    }
  };

  const getEffectiveValue = (permission: Permission): boolean => {
    if (pendingOverrides.has(permission.id)) {
      return pendingOverrides.get(permission.id)!;
    }
    return permission.granted;
  };

  const hasPendingChanges = pendingOverrides.size > 0;

  const handleSave = () => {
    const overrides = Array.from(pendingOverrides.entries()).map(([permissionId, granted]) => ({
      permissionId,
      granted,
    }));

    if (overrides.length > 0) {
      setSaveStatus('saving');
      saveOverrides.mutate(overrides);
    }
  };

  const handleClearAll = () => {
    confirm({
      title: 'Reset All Permissions',
      message: 'Are you sure you want to reset all permissions to role defaults? This will remove all custom overrides.',
      confirmLabel: 'Reset All',
      type: 'warning',
      onConfirm: () => clearOverrides.mutate(),
    });
  };

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="flex items-center gap-3">
          <WaveLoader size="sm" />
          <span className="text-sm text-gray-500">Loading permissions...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
          <i className="fas fa-exclamation-circle text-red-500 text-xl mb-2"></i>
          <p className="text-sm text-red-700">Failed to load permissions</p>
        </div>
      </div>
    );
  }

  return (
    <>
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
              <i className="fas fa-key text-purple-600 text-sm"></i>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-900">
                Permissions for {userName}
              </h3>
              <p className="text-xs text-gray-500">
                Role: {userRole} • {overrideCount} override{overrideCount !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {userRole === 'Staff' && !isPartner && (
              <button
                onClick={handleConvertToAdmin}
                disabled={convertToAdmin.isPending}
                className="px-3 py-1.5 text-xs font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 border border-blue-200 rounded-md transition-colors"
              >
                {convertToAdmin.isPending ? (
                  <i className="fas fa-circle-notch animate-spin"></i>
                ) : (
                  <>
                    <i className="fas fa-arrow-up mr-1"></i>
                    Convert to Admin
                  </>
                )}
              </button>
            )}
            {overrideCount > 0 && (
              <button
                onClick={handleClearAll}
                disabled={clearOverrides.isPending}
                className="px-3 py-1.5 text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
              >
                {clearOverrides.isPending ? (
                  <i className="fas fa-circle-notch animate-spin"></i>
                ) : (
                  <>
                    <i className="fas fa-undo mr-1"></i>
                    Reset All
                  </>
                )}
              </button>
            )}
            {onClose && (
              <button
                onClick={onClose}
                className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
              >
                <i className="fas fa-times"></i>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Permissions List */}
      <div className="max-h-[400px] overflow-y-auto">
        {Object.entries(groupedPermissions).map(([category, permissions]) => {
          const isExpanded = expandedCategories.has(category);
          const categoryOverrides = permissions.filter(p =>
            p.source === 'user_override' || pendingOverrides.has(p.id)
          ).length;

          return (
            <div key={category} className="border-b border-gray-100 last:border-b-0">
              {/* Category Header */}
              <button
                onClick={() => toggleCategory(category)}
                className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <i className={`fas ${categoryIcons[category] || 'fa-cog'} text-gray-400 text-sm w-5`}></i>
                  <span className="text-sm font-medium text-gray-900">
                    {categoryNames[category] || category}
                  </span>
                  {categoryOverrides > 0 && (
                    <span className="px-1.5 py-0.5 text-xs bg-purple-100 text-purple-700 rounded-full">
                      {categoryOverrides} override{categoryOverrides !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>
                <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'} text-gray-400 text-xs`}></i>
              </button>

              {/* Permissions in Category */}
              {isExpanded && (
                <div className="px-4 pb-3">
                  <div className="bg-gray-50 rounded-lg p-2 space-y-1">
                    {permissions.map(permission => {
                      const effectiveValue = getEffectiveValue(permission);
                      const isPending = pendingOverrides.has(permission.id);
                      const isOverride = permission.source === 'user_override' || isPending;

                      return (
                        <div
                          key={permission.id}
                          className={`flex items-center justify-between px-3 py-2 rounded-md transition-colors ${
                            isPending
                              ? 'bg-blue-50 border border-blue-200'
                              : isOverride
                              ? 'bg-purple-50 border border-purple-100'
                              : 'bg-white border border-gray-100'
                          }`}
                        >
                          <div className="flex-1 min-w-0 mr-3">
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-gray-900">{permission.name}</span>
                              {!ENFORCED_PERMISSION_IDS.has(permission.id) && (
                                <span
                                  className="px-1.5 py-0.5 text-xs bg-amber-100 text-amber-800 rounded"
                                  title="Toggling this permission does not change API access"
                                >
                                  Not enforced by the API
                                </span>
                              )}
                              {isOverride && !isPending && (
                                <span className="px-1.5 py-0.5 text-xs bg-purple-100 text-purple-700 rounded">
                                  Override
                                </span>
                              )}
                              {isPending && (
                                <span className="px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">
                                  Pending
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-gray-500 truncate" title={permission.description}>
                              {permission.description}
                            </p>
                            {permission.override && !isPending && (
                              <p className="text-xs text-purple-600 mt-0.5">
                                Set by {permission.override.grantedByName} on{' '}
                                {new Date(permission.override.grantedAt).toLocaleDateString()}
                              </p>
                            )}
                          </div>

                          <div className="flex items-center gap-3">
                            {/* Role default indicator */}
                            <span className={`text-xs px-2 py-0.5 rounded ${
                              permission.roleDefault
                                ? 'bg-green-100 text-green-700'
                                : 'bg-gray-100 text-gray-500'
                            }`}>
                              Default: {permission.roleDefault ? 'Yes' : 'No'}
                            </span>

                            {/* Toggle switch */}
                            <button
                              onClick={() => handleTogglePermission(
                                permission.id,
                                effectiveValue,
                                permission.roleDefault
                              )}
                              className={`relative w-10 h-5 rounded-full transition-colors ${
                                effectiveValue
                                  ? 'bg-green-500'
                                  : 'bg-gray-300'
                              }`}
                            >
                              <span
                                // left-0.5 anchors the knob. Without a horizontal anchor an
                                // absolutely-positioned element falls back to its STATIC position,
                                // which inside a <button> is horizontally centred -- so the knob
                                // started near the middle of the track and translate-x-5 pushed it
                                // clean outside the pill. Anchoring at 2px and translating by 20px
                                // gives a 2px inset at both ends of the 40px track.
                                className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                                  effectiveValue ? 'translate-x-5' : 'translate-x-0'
                                }`}
                              ></span>
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer with Save Button */}
      {hasPendingChanges && (
        <div className="px-4 py-3 border-t border-gray-200 bg-blue-50 flex items-center justify-between">
          <span className="text-sm text-blue-700">
            <i className="fas fa-info-circle mr-1"></i>
            {pendingOverrides.size} unsaved change{pendingOverrides.size !== 1 ? 's' : ''}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPendingOverrides(new Map())}
              className="px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 rounded-md transition-colors"
            >
              Discard
            </button>
            <button
              onClick={handleSave}
              disabled={saveOverrides.isPending}
              className={`px-4 py-1.5 text-sm font-medium text-white rounded-lg transition-colors ${
                saveStatus === 'success'
                  ? 'bg-green-500'
                  : saveStatus === 'error'
                  ? 'bg-red-500'
                  : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {saveStatus === 'saving' ? (
                <>
                  <i className="fas fa-circle-notch animate-spin mr-1"></i>
                  Saving...
                </>
              ) : saveStatus === 'success' ? (
                <>
                  <i className="fas fa-check mr-1"></i>
                  Saved
                </>
              ) : saveStatus === 'error' ? (
                <>
                  <i className="fas fa-exclamation-triangle mr-1"></i>
                  Error
                </>
              ) : (
                'Save Changes'
              )}
            </button>
          </div>
        </div>
      )}
    </div>
    <ConfirmDialog {...dialogProps} />
    </>
  );
};

export default UserPermissionsEditor;
